import axios from 'axios';
import { OMNI_ROOT_ADDRESS } from '@src/constants/urls';

export const POSITION = 'channel_cat_4a92981b';
export const DATASOURCE_ID = 'ds_cat_2981b06d';

const OPEN_API_ENDPOINT = `${OMNI_ROOT_ADDRESS}/api/omni/v1/openapi/get_position_data`;
const INVALID_RESPONSE_MESSAGE = 'invalid activity rules response';

/**
 * Encodes OMNI's nested position query using the same format/param contract as the reference project.
 * @param {string} baseUrl OMNI get_position_data endpoint.
 * @param {Object} param OMNI OpenAPI position request payload.
 * @returns {string} Request URL containing the encoded JSON payload.
 */
export const buildOpenApiUrl = (baseUrl, param) => `${baseUrl}?format&param=${encodeURIComponent(JSON.stringify(param))}`;

/**
 * Maps the fixed OMNI position response to the only ViewModel consumed by the Rules page.
 * Validation uses trimmed values only for emptiness checks; returned strings remain byte-for-byte unchanged.
 * @param {Object} responseBody Raw axios response body from get_position_data.
 * @returns {{title: string, contentHtml: string}} Stable CMS rule content.
 */
export function normalizeActivityRulesResponse(responseBody) {
  const item =
    responseBody &&
    responseBody.status === 0 &&
    responseBody.data &&
    responseBody.data[POSITION] &&
    responseBody.data[POSITION][DATASOURCE_ID] &&
    responseBody.data[POSITION][DATASOURCE_ID].result &&
    Array.isArray(responseBody.data[POSITION][DATASOURCE_ID].result.items)
      ? responseBody.data[POSITION][DATASOURCE_ID].result.items[0]
      : null;

  if (!item || typeof item.title !== 'string' || !item.title.trim() || typeof item.content !== 'string' || !item.content.trim()) {
    throw new Error(INVALID_RESPONSE_MESSAGE);
  }

  return {
    title: item.title,
    contentHtml: item.content
  };
}

/**
 * Loads the published activity rules from the fixed OMNI position and hides its transport shape from the page.
 * @param {Object} options Replaceable request dependency used by focused tests.
 * @param {Function} options.provider Axios-compatible GET provider receiving the assembled URL.
 * @returns {Promise<{title: string, contentHtml: string}>} Stable activity rules ViewModel.
 */
export function getActivityRulesAsync({ provider = axios.get } = {}) {
  const url = buildOpenApiUrl(OPEN_API_ENDPOINT, {
    lang: 'zh-Hans',
    positions: [
      {
        dataSources: [
          {
            dataSourceId: DATASOURCE_ID,
            page: 1,
            perPage: 10,
            withMeta: true
          }
        ],
        position: POSITION
      }
    ]
  });

  return Promise.resolve()
    .then(() => provider(url))
    .then(response => normalizeActivityRulesResponse(response && response.data));
}

export default {
  buildOpenApiUrl,
  normalizeActivityRulesResponse,
  getActivityRulesAsync
};
